import React, { useState, useEffect, useMemo } from 'react';
import { fetchStudents as fetchAllStudents } from '../../services/studentService';
import { sendPortalEmail } from '../../utils/emailService';
import RecipientChips from './RecipientChips';
import { buildSuggestions } from './recipients';
import './EmailFamilies.css';

// Escape user-entered text and convert newlines to <br> so a free-typed body
// renders safely inside the branded email shell. Staff are trusted, but
// escaping keeps stray angle brackets from breaking the layout.
const toBodyHtml = (text) => {
	const escaped = String(text)
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;');
	return escaped.replace(/\r\n|\r|\n/g, '<br>');
};

// Shared composer used by both surfaces (the standalone "Email families" page
// and the per-profile "Email this family" modal). The profile entry pre-fills
// `initialRecipients` and passes `studentId`; the standalone opens empty.
//
// Props:
//   initialRecipients [{name,email}]  pre-filled chips (default [])
//   studentId, familyId               optional context stamped on the log row
//   onSent  (response) => void        optional callback after a successful send
const EmailComposer = ({
	initialRecipients = [],
	studentId,
	familyId,
	onSent,
}) => {
	const [recipients, setRecipients] = useState(initialRecipients);
	const [subject, setSubject] = useState('');
	const [body, setBody] = useState('');
	const [students, setStudents] = useState([]);
	const [sending, setSending] = useState(false);
	const [result, setResult] = useState(null);
	const [error, setError] = useState('');

	useEffect(() => {
		let active = true;
		// Roster powers the typeahead. A non-admin employee without viewStudents
		// can't read it — that's fine, the manual-entry path is the fallback.
		fetchAllStudents()
			.then((all) => {
				if (active) setStudents(all);
			})
			.catch((err) => {
				console.warn('Could not load roster for typeahead (manual entry still works):', err);
			});
		return () => {
			active = false;
		};
	}, []);

	const suggestions = useMemo(() => buildSuggestions(students), [students]);

	const canSend =
		!sending && recipients.length > 0 && subject.trim() && body.trim();

	const handleSend = async () => {
		if (!canSend) return;
		setError('');
		setSending(true);
		setResult(null);
		try {
			const response = await sendPortalEmail({
				recipients: recipients.map((r) => ({ name: r.name, email: r.email })),
				subject: subject.trim(),
				bodyHtml: toBodyHtml(body),
				...(studentId ? { studentId } : {}),
				...(familyId ? { familyId } : {}),
			});
			setResult(response.results || []);
			if ((response.results || []).every((r) => r.success)) {
				if (onSent) onSent(response);
			}
		} catch (err) {
			console.error('Error sending portal email:', err);
			setError(err.message || 'Failed to send email.');
		} finally {
			setSending(false);
		}
	};

	const handleReset = () => {
		setResult(null);
		setError('');
		setRecipients(initialRecipients);
		setSubject('');
		setBody('');
	};

	if (result) {
		const sent = result.filter((r) => r.success);
		const failed = result.filter((r) => !r.success);
		return (
			<div className={`ps-alert ${failed.length === 0 ? 'ps-alert--info' : 'ps-alert--warning'}`}>
				<strong>
					{failed.length === 0 ? (
						<><i className="fa-sharp fa-solid fa-circle-check me-2" /> Email sent</>
					) : (
						<><i className="fa-sharp fa-solid fa-triangle-exclamation me-2" /> Sent with some errors</>
					)}
				</strong>
				<hr />
				{sent.length > 0 && (
					<div className="mb-3">
						<strong style={{ color: 'var(--cyv-teal-800)' }}>
							<i className="fa-sharp fa-solid fa-check me-1" />
							Sent to {sent.length} recipient{sent.length !== 1 ? 's' : ''}:
						</strong>
						<div className="small mt-2">
							{sent.map((r, idx) => (
								<div key={idx}>• {r.email}</div>
							))}
						</div>
					</div>
				)}
				{failed.length > 0 && (
					<div>
						<strong style={{ color: '#b23b3b' }}>
							<i className="fa-sharp fa-solid fa-xmark me-1" />
							Failed for {failed.length} recipient{failed.length !== 1 ? 's' : ''}:
						</strong>
						<div className="small mt-2">
							{failed.map((r, idx) => (
								<div key={idx}>• {r.email}: {r.error}</div>
							))}
						</div>
					</div>
				)}
				<button type="button" className="btn-cyv-teal mt-3" onClick={handleReset}>
					<i className="fa-sharp fa-solid fa-pen-to-square" /> Compose another
				</button>
			</div>
		);
	}

	return (
		<form className="ef-composer" onSubmit={(e) => e.preventDefault()}>
			{error && (
				<div className="ps-alert ps-alert--danger">
					<i className="fa-sharp fa-solid fa-triangle-exclamation me-2" />
					{error}
				</div>
			)}

			<div className="ps-field">
				<label className="ps-label">To</label>
				<RecipientChips
					recipients={recipients}
					onChange={setRecipients}
					suggestions={suggestions}
					disabled={sending}
				/>
				<small className="text-muted">
					Type a student or family name to add their guardians, or type an email and
					press comma to add it.
				</small>
			</div>

			<div className="ps-field">
				<label className="ps-label" htmlFor="ef-subject">Subject</label>
				<input
					id="ef-subject"
					type="text"
					className="ps-input"
					value={subject}
					onChange={(e) => setSubject(e.target.value)}
					placeholder="e.g., A quick note about next week"
					disabled={sending}
					maxLength={150}
				/>
			</div>

			<div className="ps-field">
				<label className="ps-label" htmlFor="ef-body">Message</label>
				<textarea
					id="ef-body"
					className="ps-textarea"
					rows={10}
					value={body}
					onChange={(e) => setBody(e.target.value)}
					placeholder="Write your message here…"
					disabled={sending}
				/>
				{/* Templates: deferred to E4 Layer 1 v2 — seed subject/body from an
				    emailTemplates entry here once variable substitution is wired. */}
			</div>

			<div className="ef-composer__actions">
				<span className="ps-badge ps-badge--info">
					{recipients.length} recipient{recipients.length !== 1 ? 's' : ''}
				</span>
				<button
					type="button"
					className="btn-cyv-teal"
					onClick={handleSend}
					disabled={!canSend}>
					{sending ? (
						<><span className="ps-spinner ef-btn-spinner" /> Sending…</>
					) : (
						<><i className="fa-sharp fa-solid fa-paper-plane" /> Send email</>
					)}
				</button>
			</div>
		</form>
	);
};

export default EmailComposer;
